import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Dialog, DialogContent, Slide, Typography, useMediaQuery, useTheme } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useSelector } from 'react-redux';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { TransitionProps } from '@mui/material/transitions';
import { FONT_ENUM } from '../../types/global/fonts.types';
import CloseIcon from '../../components/CustomSVGIcons/CloseIcon';
import { AccountSubscriptions } from './AccountSubscriptions';
import {
    subscriptionModalEntitlementFilterSelector,
    subscriptionModalAllowedPlanClassesSelector,
} from '../../selectors/selectors';
import { SUBSCRIPTION_MODAL_Z_INDEX } from '../../constants/subscriptionModalZIndex';
import type { CreatorEntitlementKey, CreatorPlanClass } from '../../utils/planEntitlements';
import { plansSelector } from '../EsSettings/selector';
import { DEFAULT_CREATOR_TRIAL_DAYS, resolveCreatorTrialDays } from '../../utils/creatorSubscriptionTrial';
import { resolveFeatureGateHeadline } from '../../hook/usePlanEntitlementGates';

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const COUNTDOWN_SECONDS = 60; // 1 minute

const SUBSCRIPTION_BENEFITS = [
    'Keep more of every campaign payout — platform fee waived',
    'Be the first to know about new jobs & campaigns',
    'Priority job placement in brand searches',
];

/** Extra perk only on premium / View plans trial hero */
const TRIAL_VIEW_PLANS_EXTRA_BENEFIT = 'Instagram automations';

export interface IAccountSubscriptionModal {
    open: boolean;
    onClose: VoidFunction;
    /** Called after a successful subscribe action (before onClose). */
    onDone?: VoidFunction;
    /** Formatted USD amount saved on this campaign (e.g. "$21.43"). Renders rich hero when provided. */
    saveAmountFormatted?: string;
    /**
     * `trial` — premium / View plans: "Start a Free N-Day trial".
     * `pro` — premium / trial already used: campaign needs Pro (no trial pitch).
     * `save` (default) — payout upsell: "Subscribe & Earn $X More".
     */
    heroVariant?: 'save' | 'trial' | 'pro';
    /** When set, shows a skip button at the bottom with "No Thanks" copy. */
    onSkip?: VoidFunction;
    /** Paystack callback URL after subscription completes (e.g. campaign details page). */
    paystackCallbackUrl?: string;
    /** Campaign feature image shown as a blurred background in the hero section. */
    campaignImageUrl?: string;
    /** Prefer over Redux filter — show plans that grant at least one entitlement. */
    requireAnyEntitlement?: CreatorEntitlementKey[];
    /** Prefer over Redux filter — show only plans that grant every listed entitlement. */
    requireAllEntitlements?: CreatorEntitlementKey[];
    /** Prefer over Redux — limit catalog to these plan classes (Pro Pass → Pro + Pro Plus). */
    allowedPlanClasses?: CreatorPlanClass[];
    /** Override the pill label in the hero (e.g. "Subscription required"). Forces hero to show. */
    heroPill?: string;
    /** Override the headline in the hero. Forces hero to show. */
    heroTitle?: React.ReactNode;
    /** Override the subcopy line below the headline in the hero. */
    heroSubcopy?: string;
}

const AccountSubscriptionModal: React.FC<IAccountSubscriptionModal> = ({
    onClose,
    onDone,
    open,
    saveAmountFormatted,
    heroVariant = 'save',
    onSkip,
    paystackCallbackUrl,
    campaignImageUrl,
    requireAnyEntitlement,
    requireAllEntitlements,
    allowedPlanClasses,
    heroPill,
    heroTitle,
    heroSubcopy,
}) => {
    const { themeStyle } = useThemeContext();
    const themeui = useTheme();
    const fullScreen = useMediaQuery(themeui.breakpoints.down('md'));
    const t = theme[themeStyle];
    const plans = useSelector(plansSelector);
    const trialDays = useMemo(() => {
        const fromPlan = plans?.find(p => p?.trial?.days != null || p?.trial?.available)?.trial;
        return resolveCreatorTrialDays(fromPlan ?? { days: DEFAULT_CREATOR_TRIAL_DAYS });
    }, [plans]);
    const showTrialHero = heroVariant === 'trial';
    const showProHero = heroVariant === 'pro';
    const showPremiumCampaignHero = showTrialHero || showProHero;
    const showHero = showPremiumCampaignHero || Boolean(saveAmountFormatted) || Boolean(heroTitle);
    const heroBenefits = showPremiumCampaignHero
        ? [...SUBSCRIPTION_BENEFITS, TRIAL_VIEW_PLANS_EXTRA_BENEFIT]
        : SUBSCRIPTION_BENEFITS;
    const reduxEntitlementFilter = useSelector(subscriptionModalEntitlementFilterSelector);
    const reduxAllowedPlanClasses = useSelector(subscriptionModalAllowedPlanClassesSelector);
    const anyFilter = requireAnyEntitlement ?? (requireAllEntitlements?.length ? undefined : reduxEntitlementFilter);
    const allFilter = requireAllEntitlements;
    const planClassFilter = allowedPlanClasses ?? reduxAllowedPlanClasses;
    const hasEntitlementFilter = Boolean(allFilter?.length || anyFilter?.length);
    const hasPlanClassFilter = Boolean(planClassFilter?.length);
    /** Campaign unlocks use requireAll; feature gates (Create Project, etc.) use requireAny / Redux. */
    const isCampaignEntitlementPrompt = Boolean(allFilter?.length);
    const isProPassPrompt = Boolean(hasPlanClassFilter && !hasEntitlementFilter);
    const featureGateHeadline =
        !showHero && !isCampaignEntitlementPrompt && !isProPassPrompt
            ? resolveFeatureGateHeadline(anyFilter)
            : isProPassPrompt
            ? 'Pro Pass requires Pro or Pro Plus'
            : null;
    const minimalHeaderSubcopy =
        hasEntitlementFilter || hasPlanClassFilter
            ? isCampaignEntitlementPrompt
                ? 'Choose a plan that includes the features this campaign requires.'
                : isProPassPrompt
                ? 'Choose Pro or Pro Plus to unlock this masterclass.'
                : 'Choose a plan that unlocks this feature.'
            : "You've reached your free plan limit for this month. Choose a plan to keep going.";

    const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const countdownKilledRef = useRef(false);

    useEffect(() => {
        if (!open || !onSkip) return;
        setSecondsLeft(COUNTDOWN_SECONDS);
        countdownKilledRef.current = false;
        intervalRef.current = setInterval(() => {
            setSecondsLeft(s => (s <= 1 ? 0 : s - 1));
        }, 1000);
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [open, onSkip]);

    useEffect(() => {
        if (secondsLeft === 0 && onSkip && !countdownKilledRef.current) {
            countdownKilledRef.current = true;
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            onSkip();
        }
    }, [secondsLeft, onSkip]);

    const killCountdown = () => {
        countdownKilledRef.current = true;
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    };

    const pauseCountdown = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    };

    const resumeCountdown = () => {
        if (countdownKilledRef.current || intervalRef.current) return;
        intervalRef.current = setInterval(() => {
            setSecondsLeft(s => (s <= 1 ? 0 : s - 1));
        }, 1000);
    };

    const mins = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
    const secs = String(secondsLeft % 60).padStart(2, '0');
    const progress = (secondsLeft / COUNTDOWN_SECONDS) * 100;
    const countdownUrgent = secondsLeft <= 30;
    const countdownConicGradient = countdownUrgent
        ? 'conic-gradient(from 0deg, #f97316, #ef4444, #fb923c, #facc15, #f97316, #ef4444, #facc15, #f97316)'
        : 'conic-gradient(from 0deg, #a855f7, #7c3aed, #c084fc, #ec4899, #ffc965, #facc15, #fde047, #ffc965, #ec4899, #a855f7)';
    const countdownDigitGradient = countdownUrgent
        ? 'linear-gradient(90deg, #fb923c 0%, #facc15 50%, #ef4444 100%)'
        : 'linear-gradient(90deg, #c084fc 0%, #ffc965 48%, #fde047 100%)';
    const countdownProgressGradient = countdownUrgent
        ? 'linear-gradient(90deg, #f97316 0%, #ef4444 50%, #facc15 100%)'
        : 'linear-gradient(90deg, #a855f7 0%, #ec4899 40%, #ffc965 75%, #facc15 100%)';

    return (
        <Dialog
            scroll="paper"
            open={open}
            onClose={onClose}
            TransitionComponent={Transition}
            fullWidth
            fullScreen={fullScreen}
            maxWidth={hasEntitlementFilter ? 'md' : 'lg'}
            PaperProps={{
                sx: {
                    bgcolor: '#0e0e0e',
                    backgroundImage: 'none',
                    width: '100%',
                    maxWidth: fullScreen ? '100%' : hasEntitlementFilter ? 760 : 1120,
                    borderRadius: fullScreen ? 0 : '12px',
                    border: fullScreen ? 'none' : '1px solid #484847',
                    m: 0,
                    maxHeight: '100vh',
                    height: fullScreen ? '100%' : 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                },
            }}
            sx={{
                outline: 'none',
                bgcolor: 'rgba(0,0,0,0.72)',
                zIndex: SUBSCRIPTION_MODAL_Z_INDEX,
            }}
            style={{ zIndex: SUBSCRIPTION_MODAL_Z_INDEX }}>
            {showHero ? (
                /* Rich hero header for campaign upsell */
                <Box
                    sx={{
                        position: 'relative',
                        flexShrink: 0,
                        px: { xs: 2.5, sm: 4 },
                        pt: { xs: 3, sm: 4 },
                        pb: { xs: 2.5, sm: 3 },
                        overflow: 'hidden',
                    }}>
                    {/* Blurred campaign image background */}
                    {campaignImageUrl && (
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                backgroundImage: `url(${campaignImageUrl})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                filter: 'blur(8px)',
                                transform: 'scale(1.12)',
                                opacity: 0.58,
                                zIndex: 0,
                            }}
                        />
                    )}
                    {/* Gradient overlay — bottom fades to solid dark to blend with plan cards below */}
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            background: campaignImageUrl
                                ? 'linear-gradient(180deg, rgba(14,14,14,0.35) 0%, rgba(14,14,14,0.72) 60%, rgba(14,14,14,1) 100%)'
                                : 'linear-gradient(135deg, rgba(255,201,101,0.10) 0%, rgba(255,140,50,0.06) 60%, rgba(14,14,14,0) 100%)',
                            zIndex: 0,
                        }}
                    />
                    {/* Close */}
                    <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}>
                        <CloseIcon onClick={onClose} style={{ cursor: 'pointer' }} stroke={t.tag_white} />
                    </Box>

                    {/* Save pill */}
                    <Box
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 999,
                            bgcolor: 'rgba(255,201,101,0.12)',
                            border: '1px solid rgba(255,201,101,0.3)',
                            mb: 1.5,
                        }}>
                        <Box
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                bgcolor: '#ffc965',
                                boxShadow: '0 0 6px #ffc965',
                            }}
                        />
                        <Typography
                            sx={{
                                fontSize: 11,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color: '#ffc965',
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                            }}>
                            {heroPill ??
                                (showTrialHero ? 'Free trial' : showProHero ? 'Pro required' : 'Exclusive benefit')}
                        </Typography>
                    </Box>

                    {/* Headline */}
                    <Typography
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                            fontSize: { xs: 22, sm: 28 },
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            color: '#fff',
                            lineHeight: 1.2,
                            mb: 0.5,
                        }}>
                        {heroTitle ??
                            (showTrialHero ? (
                                <>
                                    Start a Free{' '}
                                    <Box component="span" sx={{ color: t.highlight ?? '#ffc965' }}>
                                        {trialDays}-Day
                                    </Box>{' '}
                                    trial
                                </>
                            ) : showProHero ? (
                                <>
                                    This campaign requires{' '}
                                    <Box component="span" sx={{ color: t.highlight ?? '#ffc965' }}>
                                        Pro tools
                                    </Box>
                                </>
                            ) : (
                                <>
                                    Subscribe &amp; Earn{' '}
                                    <Box component="span" sx={{ color: '#ffc965' }}>
                                        {saveAmountFormatted}
                                    </Box>{' '}
                                    More
                                </>
                            ))}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 13,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: 'rgba(255,255,255, 0.95)',
                            mb: 2.5,
                        }}>
                        {heroSubcopy ??
                            (showTrialHero
                                ? 'Unlock this premium campaign — plus these perks:'
                                : showProHero
                                ? 'Subscribe to apply — plus these perks:'
                                : 'when accepted to this campaign — plus these perks:')}
                    </Typography>

                    {/* Benefits + countdown row */}
                    <Box
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'stretch', sm: 'flex-start' },
                            gap: 3,
                        }}>
                        {/* Benefits list */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {heroBenefits.map(benefit => (
                                <Box key={benefit} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                    <Box
                                        sx={{
                                            mt: '2px',
                                            width: 18,
                                            height: 18,
                                            borderRadius: '50%',
                                            bgcolor: 'rgba(255,201,101,0.15)',
                                            flexShrink: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}>
                                        <CheckIcon sx={{ fontSize: 11, color: '#ffc965' }} />
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: 'rgba(255,255,255,0.75)',
                                            lineHeight: 1.5,
                                        }}>
                                        {benefit}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        {/* Countdown card — only in campaign apply flow */}
                        {onSkip && (
                            <Box
                                sx={{
                                    flexShrink: 0,
                                    width: { xs: '100%', sm: 148 },
                                    position: 'relative',
                                    p: '2.5px',
                                    borderRadius: 2.5,
                                    overflow: 'hidden',
                                    isolation: 'isolate',
                                    boxShadow: countdownUrgent
                                        ? '0 0 18px rgba(239,68,68,0.35)'
                                        : '0 0 18px rgba(168,85,247,0.28), 0 0 28px rgba(255,201,101,0.18)',
                                }}>
                                {/* Full-perimeter spinning fill — square sized from card width so wide mobile stays covered */}
                                <Box
                                    aria-hidden
                                    sx={{
                                        position: 'absolute',
                                        zIndex: 0,
                                        top: '50%',
                                        left: '50%',
                                        width: 'max(340%, 520px)',
                                        aspectRatio: '1 / 1',
                                        height: 'auto',
                                        background: countdownConicGradient,
                                        '@keyframes countdownBorderSpin': {
                                            '0%': { transform: 'translate(-50%, -50%) rotate(0deg)' },
                                            '100%': { transform: 'translate(-50%, -50%) rotate(360deg)' },
                                        },
                                        animation: 'countdownBorderSpin 16s linear infinite',
                                        willChange: 'transform',
                                        '@media (prefers-reduced-motion: reduce)': {
                                            animation: 'none',
                                            transform: 'translate(-50%, -50%)',
                                        },
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: 'relative',
                                        zIndex: 1,
                                        p: 1.5,
                                        borderRadius: 2,
                                        bgcolor: 'rgba(14,14,14,0.96)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 0.75,
                                    }}>
                                    <Typography
                                        sx={{
                                            fontSize: 9,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            background: countdownDigitGradient,
                                            backgroundClip: 'text',
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            color: 'transparent',
                                            letterSpacing: '0.1em',
                                            textTransform: 'uppercase',
                                        }}>
                                        Limited time offer
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 30,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            lineHeight: 1,
                                            letterSpacing: '0.04em',
                                            background: countdownDigitGradient,
                                            backgroundClip: 'text',
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            color: 'transparent',
                                            filter: 'drop-shadow(0 0 10px rgba(255,201,101,0.35))',
                                        }}>
                                        {mins}:{secs}
                                    </Typography>
                                    {/* Progress bar */}
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: 4,
                                            borderRadius: 99,
                                            bgcolor: 'rgba(255,255,255,0.1)',
                                            overflow: 'hidden',
                                        }}>
                                        <Box
                                            sx={{
                                                height: '100%',
                                                width: `${progress}%`,
                                                borderRadius: 99,
                                                background: countdownProgressGradient,
                                                transition: 'width 1s linear',
                                                boxShadow: '0 0 8px rgba(255,201,101,0.45)',
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            ) : (
                /* Minimal header for non-campaign contexts */
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 2,
                        px: { xs: 2, sm: 3 },
                        pt: 2.5,
                        pb: 1,
                        flexShrink: 0,
                    }}>
                    <Box sx={{ minWidth: 0, maxWidth: 560 }}>
                        {featureGateHeadline ? (
                            <Typography
                                sx={{
                                    fontSize: { xs: 18, sm: 20 },
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: t.tag_white ?? '#fff',
                                    lineHeight: 1.3,
                                    mb: 0.5,
                                }}>
                                {featureGateHeadline}
                            </Typography>
                        ) : null}
                        <Typography
                            sx={{
                                fontSize: 13,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                color: t.form_label,
                                lineHeight: 1.5,
                            }}>
                            {minimalHeaderSubcopy}
                        </Typography>
                    </Box>
                    <CloseIcon onClick={onClose} style={{ cursor: 'pointer', flexShrink: 0 }} stroke={t.tag_white} />
                </Box>
            )}

            <DialogContent
                sx={{
                    pt: showHero ? 2 : 0,
                    px: { xs: 1, sm: 2 },
                    pb: onSkip ? 1.5 : 3,
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch',
                }}>
                <AccountSubscriptions
                    onDone={() => {
                        onDone?.();
                        onClose();
                    }}
                    onSubscribeStart={killCountdown}
                    onPlanSelect={pauseCountdown}
                    onPlanDeselect={resumeCountdown}
                    isPromptMode
                    paystackCallbackUrl={paystackCallbackUrl}
                    requireAnyEntitlement={anyFilter}
                    requireAllEntitlements={allFilter}
                    allowedPlanClasses={planClassFilter}
                />
            </DialogContent>

            {onSkip && (
                <Box
                    sx={{
                        flexShrink: 0,
                        px: { xs: 2, sm: 3 },
                        pt: 1.25,
                        pb: { xs: 'max(12px, env(safe-area-inset-bottom))', sm: 2 },
                        borderTop: '1px solid rgba(255,255,255,0.08)',
                        bgcolor: 'rgba(14,14,14,0.96)',
                        display: 'flex',
                        justifyContent: 'center',
                        zIndex: 2,
                    }}>
                    <Button
                        onClick={onSkip}
                        fullWidth={fullScreen}
                        sx={{
                            fontSize: { xs: 13, sm: 12 },
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: 'rgba(255,255,255,0.55)',
                            textTransform: 'none',
                            textDecoration: 'underline',
                            py: { xs: 1.25, sm: 0.75 },
                            maxWidth: fullScreen ? '100%' : 'unset',
                            '&:hover': { color: 'rgba(255,255,255,0.85)', bgcolor: 'transparent' },
                        }}>
                        No Thanks — Stay as I am
                    </Button>
                </Box>
            )}
        </Dialog>
    );
};

export { AccountSubscriptionModal };
